import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ArrowRight, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function DemoForm() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    farm_type: "",
    herd_size: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.farm_type) {
      toast.error("Please fill in name, email and farm type.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/demo-requests`, form);
      toast.success("Thanks — we'll be in touch shortly.");
      setDone(true);
      setForm({ name: "", email: "", farm_type: "", herd_size: "" });
    } catch (err) {
      const msg =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        "Something went wrong. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Please check your details.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      data-testid="demo-form-card"
      className="bg-white rounded-md p-7 sm:p-9 shadow-sm border border-[#D5D1C8]"
    >
      <div className="mb-6">
        <span className="inline-block text-xs font-bold tracking-[0.2em] uppercase text-[#D96C32]">
          Demo Walkthrough
        </span>
        <h3 className="mt-2 font-display font-bold text-2xl text-[#1A1D1A]">
          See it working in real yards
        </h3>
        <p className="mt-2 text-sm text-[#4A4F4A]">
          We'll set up a short walkthrough at a time that suits the season.
        </p>
      </div>

      {done ? (
        <div
          data-testid="demo-form-success"
          className="rounded-md border border-[#2D4A22] bg-[#F9F8F6] p-6 text-center"
        >
          <p className="font-display font-bold text-lg text-[#2D4A22]">
            Request received.
          </p>
          <p className="mt-2 text-sm text-[#4A4F4A]">
            We'll reach out within one business day. Cheers.
          </p>
          <button
            type="button"
            data-testid="demo-form-reset"
            onClick={() => setDone(false)}
            className="mt-4 text-sm font-semibold text-[#D96C32] hover:underline"
          >
            Submit another →
          </button>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label htmlFor="demo-name" className="text-sm font-semibold text-[#1A1D1A]">
              Your name
            </Label>
            <Input
              id="demo-name"
              data-testid="demo-input-name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Jane Smith"
              className="mt-1.5 h-12 border-[#D5D1C8] focus-visible:ring-[#2D4A22]"
              required
            />
          </div>

          <div>
            <Label htmlFor="demo-email" className="text-sm font-semibold text-[#1A1D1A]">
              Email
            </Label>
            <Input
              id="demo-email"
              data-testid="demo-input-email"
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              placeholder="[email protected]"
              className="mt-1.5 h-12 border-[#D5D1C8] focus-visible:ring-[#2D4A22]"
              required
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-sm font-semibold text-[#1A1D1A]">
                Farm type
              </Label>
              <Select
                value={form.farm_type}
                onValueChange={(v) => setForm({ ...form, farm_type: v })}
              >
                <SelectTrigger
                  data-testid="demo-input-farm-type"
                  className="mt-1.5 h-12 border-[#D5D1C8] focus:ring-[#2D4A22]"
                >
                  <SelectValue placeholder="Select…" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Dairy" data-testid="farm-type-dairy">
                    Dairy
                  </SelectItem>
                  <SelectItem value="Beef" data-testid="farm-type-beef">
                    Beef
                  </SelectItem>
                  <SelectItem value="Contractor" data-testid="farm-type-contractor">
                    Contractor
                  </SelectItem>
                  <SelectItem value="Other" data-testid="farm-type-other">
                    Other
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="demo-herd" className="text-sm font-semibold text-[#1A1D1A]">
                Herd size <span className="font-normal text-[#4A4F4A]">(optional)</span>
              </Label>
              <Input
                id="demo-herd"
                data-testid="demo-input-herd-size"
                value={form.herd_size}
                onChange={(e) => setForm({ ...form, herd_size: e.target.value })}
                placeholder="e.g. 450"
                className="mt-1.5 h-12 border-[#D5D1C8] focus-visible:ring-[#2D4A22]"
              />
            </div>
          </div>

          <Button
            type="submit"
            data-testid="demo-submit-btn"
            disabled={loading}
            className="w-full bg-[#D96C32] hover:bg-[#B85A28] text-white font-bold h-13 py-4 rounded-md shadow-sm hover:-translate-y-0.5 transition-all"
          >
            {loading ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Sending…
              </>
            ) : (
              <>
                Request Demo
                <ArrowRight className="w-5 h-5 ml-2" />
              </>
            )}
          </Button>
          <p className="text-xs text-[#4A4F4A] text-center">
            No spam. We only use your details to set up the walkthrough.
          </p>
        </form>
      )}
    </div>
  );
}

function ContactForm() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please complete all fields.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contacts`, form);
      toast.success("Message sent. Thanks.");
      setDone(true);
      setForm({ name: "", email: "", message: "" });
    } catch (err) {
      const msg =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        "Something went wrong. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Please check your details.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      data-testid="contact-form-card"
      className="bg-[#1F3617] rounded-md p-7 sm:p-9 border border-white/10"
    >
      <div className="mb-6">
        <span className="inline-block text-xs font-bold tracking-[0.2em] uppercase text-[#F2C76A]">
          Contact Team
        </span>
        <h3 className="mt-2 font-display font-bold text-2xl text-white">
          Got a specific question?
        </h3>
        <p className="mt-2 text-sm text-white/75">
          Drop us a message — a real person on the team will reply.
        </p>
      </div>

      {done ? (
        <div
          data-testid="contact-form-success"
          className="rounded-md border border-[#F2C76A]/40 bg-white/5 p-6 text-center"
        >
          <p className="font-display font-bold text-lg text-[#F2C76A]">
            Got it.
          </p>
          <p className="mt-2 text-sm text-white/80">
            We'll get back to you shortly.
          </p>
          <button
            type="button"
            data-testid="contact-form-reset"
            onClick={() => setDone(false)}
            className="mt-4 text-sm font-semibold text-[#F2C76A] hover:underline"
          >
            Send another →
          </button>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label htmlFor="contact-name" className="text-sm font-semibold text-white">
              Your name
            </Label>
            <Input
              id="contact-name"
              data-testid="contact-input-name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Jane Smith"
              className="mt-1.5 h-12 bg-white/10 border-white/20 text-white placeholder:text-white/50 focus-visible:ring-[#F2C76A]"
              required
            />
          </div>
          <div>
            <Label htmlFor="contact-email" className="text-sm font-semibold text-white">
              Email
            </Label>
            <Input
              id="contact-email"
              data-testid="contact-input-email"
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              placeholder="[email protected]"
              className="mt-1.5 h-12 bg-white/10 border-white/20 text-white placeholder:text-white/50 focus-visible:ring-[#F2C76A]"
              required
            />
          </div>
          <div>
            <Label htmlFor="contact-message" className="text-sm font-semibold text-white">
              Message
            </Label>
            <Textarea
              id="contact-message"
              data-testid="contact-input-message"
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              placeholder="Tell us about your operation or what you'd like to see…"
              rows={4}
              className="mt-1.5 bg-white/10 border-white/20 text-white placeholder:text-white/50 focus-visible:ring-[#F2C76A]"
              required
            />
          </div>
          <Button
            type="submit"
            data-testid="contact-submit-btn"
            disabled={loading}
            className="w-full bg-[#F2C76A] hover:bg-[#E0B85A] text-[#1A1D1A] font-bold py-4 h-auto rounded-md shadow-sm hover:-translate-y-0.5 transition-all"
          >
            {loading ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Sending…
              </>
            ) : (
              <>Send Message</>
            )}
          </Button>
        </form>
      )}
    </div>
  );
}

export default function CTASection() {
  return (
    <section
      id="cta"
      data-testid="cta-section"
      className="bg-[#2D4A22] py-20 md:py-28 relative overflow-hidden"
    >
      <div
        className="absolute inset-0 opacity-[0.07]"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='60' height='60' viewBox='0 0 60 60'><path d='M0 30h60M30 0v60' stroke='white' stroke-width='0.5'/></svg>\")",
        }}
        aria-hidden
      />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-14">
          <h2
            data-testid="cta-headline"
            className="font-display font-bold text-3xl sm:text-4xl lg:text-5xl text-white leading-tight"
          >
            Want to see Arvo in action?
          </h2>
          <p className="mt-5 text-lg text-white/80 leading-relaxed">
            Independent, provider-neutral, and built with farmers and service
            providers in mind. Get a walkthrough or send us a question — a real
            person on the team will reply.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8 max-w-5xl mx-auto">
          <DemoForm />
          <ContactForm />
        </div>
      </div>
    </section>
  );
}
